import React, { useState, useEffect, useCallback } from 'react';
import { Shield, RefreshCw, Activity } from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';
import { systemHealthService } from '../services/systemHealthService';
import { supabase } from '../services/supabaseService';
import type { IntegrationHealth, JobsSummary } from '../services/systemHealthService';
import SystemHealthOverview from './system/SystemHealthOverview';
import IntegrationHealthPanel from './system/IntegrationHealthPanel';
import RecentSystemEvents from './system/RecentSystemEvents';
import IntegrationJobsPanel from './system/IntegrationJobsPanel';

function formatTimestamp(date: Date): string {
  return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

async function getLeadsCreatedToday(): Promise<number> {
  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);

  const { count, error } = await supabase
    .from('leads')
    .select('*', { count: 'exact', head: true })
    .gte('created_at', startOfDay.toISOString());

  if (error) throw error;
  return count ?? 0;
}

const SystemControlCenter: React.FC = () => {
  const { isSuperAdmin, loading: permissionsLoading } = usePermissions();

  const [integrations, setIntegrations] = useState<IntegrationHealth[]>([]);
  const [jobsSummary, setJobsSummary] = useState<JobsSummary | null>(null);
  const [criticalCount, setCriticalCount] = useState<number | null>(null);
  const [leadsToday, setLeadsToday] = useState<number | null>(null);
  const [isLoadingOverview, setIsLoadingOverview] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [healthError, setHealthError] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const loadOverviewData = useCallback(async () => {
    setIsLoadingOverview(true);
    try {
      const [healthData, summary, critical, leads] = await Promise.allSettled([
        systemHealthService.getIntegrationHealth(),
        systemHealthService.getIntegrationJobsSummary(),
        systemHealthService.getCriticalEventCount24h(),
        getLeadsCreatedToday(),
      ]);

      setHealthError(healthData.status === 'rejected');
      setIntegrations(healthData.status === 'fulfilled' ? healthData.value : []);
      setJobsSummary(summary.status === 'fulfilled' ? summary.value : null);
      setCriticalCount(critical.status === 'fulfilled' ? critical.value : null);
      setLeadsToday(leads.status === 'fulfilled' ? leads.value : null);
      const failed = [healthData, summary, critical, leads].some(r => r.status === 'rejected');
      setLoadError(failed);
      setLastRefreshed(failed ? null : new Date());
    } catch {
      setLoadError(true);
      setLastRefreshed(null);
    } finally {
      setIsLoadingOverview(false);

    }
  }, []);

  useEffect(() => {
    if (!permissionsLoading && isSuperAdmin()) {
      loadOverviewData();
    }
  }, [permissionsLoading]);

  const handleRefreshAll = async () => {
    setRefreshVersion(v => v + 1);
    setIsRefreshing(true);
    await loadOverviewData();
    setIsRefreshing(false);
  };

  if (permissionsLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400" />
      </div>
    );
  }

  if (!isSuperAdmin()) {
    return (
      <div className="p-8 text-center">
        <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 mb-2">Acceso Restringido</h2>
        <p className="text-gray-500">Solo los superadministradores pueden acceder al Control Operativo.</p>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50">
      <div className="max-w-screen-xl mx-auto px-6 py-6 space-y-8">

        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-900 rounded-xl">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Control Operativo</h1>
              <p className="text-xs text-gray-400">
                {lastRefreshed ? `Resumen actualizado a las ${formatTimestamp(lastRefreshed)}` : 'Resumen pendiente de una lectura completa'}
              </p>
            </div>
          </div>
          <button
            onClick={handleRefreshAll}
            disabled={isRefreshing}
            className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 bg-white border border-gray-200 hover:border-gray-300 px-4 py-2 rounded-lg transition-all disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Actualizar todo
          </button>
        </div>

        {loadError && <p role="alert" className="bg-red-50 text-red-800 p-4 rounded-lg">No se pudieron consultar todos los datos. Los indicadores sin información no significan que todo esté funcionando.</p>}
        {/* Row 1: Overview cards */}
        <section>
          <SystemHealthOverview
            healthUnavailable={healthError}
            criticalCount24h={criticalCount}
            integrations={integrations}
            jobsSummary={jobsSummary}
            leadsToday={leadsToday}
            isLoading={isLoadingOverview}
          />
        </section>

        {/* Row 2: Integration health */}
        <section className="bg-white rounded-2xl border border-gray-200 p-6">
          <IntegrationHealthPanel
            integrations={integrations}
            onRefresh={loadOverviewData}
          />
        </section>

        {/* Row 3: Events + Jobs side by side on large screens */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <section className="bg-white rounded-2xl border border-gray-200 p-6">
            <RecentSystemEvents refreshVersion={refreshVersion} />
          </section>
          <section className="bg-white rounded-2xl border border-gray-200 p-6">
            <IntegrationJobsPanel refreshVersion={refreshVersion} onChanged={loadOverviewData} />
          </section>
        </div>

      </div>
    </div>
  );
};

export default SystemControlCenter;
